import { isOption, isOptionArray, Option } from "api/common/types/common.types";
import { toSnakeCase } from "api/common/utils/transform.utils";
import {
  DateRange,
  DynamicDateRange,
  isDateRangeObject,
  isDynamicDateRangeObject,
} from "components/Forms/utils/dates.utils";
import { isNumber, isBoolean, isString, isStringArray } from "utils/typeGuards";

import { DjangoPaginationAdapter } from "./pagination.adapters";
import { DjangoQueryParams } from "./pagination.types";
import {
  FilterSet,
  OverridesMap,
  QueryAdapter,
  QueryAdapterConfig,
  TransformsMap,
} from "./queryParam.types";

const createParam = (key: string, value: string) => ({ [key]: value });

// standard set of defaults that should cover most data types
const defaultTransforms: TransformsMap = {
  // input: ("isCute", true)
  // output: {is_cute: "true"}
  boolean: (key: string, value: boolean) => createParam(key, value.toString()),

  // input: ("age", 5)
  // output: {age: "5"}
  number: (key: string, value: boolean) => createParam(key, value.toString()),

  // input: ("catName", "keaton")
  // output: {cat_name: "Keaton"}
  string: (key: string, value: string) => createParam(key, value),

  // input: ("favoriteToys", ["mouse", "spring", "laser"])
  // output: {favorite_toys: "mouse,spring,laser"}
  stringArray: (key: string, arr: string[]) => createParam(key, arr.join(",")),

  // input: ("favoriteToys", [1,2,3])
  // output: {favorite_toys: "1,2,3"}
  generalArray: (key: string, arr: any[]) =>
    createParam(key, arr.map((item) => item.toString()).join(",")),

  // input: ("food", {label: "tuna", value: 67})
  // output: {food: "67"}
  option: (key: string, opt: Option) => createParam(key, opt.value.toString()),

  // input: ("friends", [{label: "Navi", value: 123}, {label: "Goldberry", value: 456}])
  // output: {friends: "123,456"}
  optionArray: (key: string, arr: Option[]) => {
    const options = arr.map((opt) => opt.value.toString()).join(",");
    return createParam(key, options);
  },

  /// date ///
  // input: ("adopted_on", {after: "1/1/2025", before: "1/1/2026"})
  // output: {adopted_on_after: "1/1/2025", adopted_on_before: "1/1/2026"}
  /// dynamic date ///
  // input ("date", {after: "1/1/2025", before: "1/1/2026", field: "birthdate"})
  // output: {birthdate_after: "1/1/2025", birthdate_before: "1/1/2026"}
  date: (snakeKey: string, value: DateRange | DynamicDateRange) => {
    const result: Record<string, string> = {};

    let dateKey = snakeKey;
    if (isDynamicDateRangeObject(value)) {
      const { field } = value;
      dateKey = isOption(field) ? field.value : field;
    }

    value.after && (result[`${dateKey}_after`] = value.after);
    value.before && (result[`${dateKey}_before`] = value.before);

    return result;
  },
} as const;
type DefaultTransforms = typeof defaultTransforms;

// checks for the supported types. should have one for every type in your transform map
const defaultTypeGetter = (value: unknown): keyof DefaultTransforms | null => {
  // primitives
  if (isBoolean(value)) return "boolean";
  if (isNumber(value)) return "number";
  if (isString(value)) return "string";

  // custom objects
  if (isOption(value)) return "option";
  if (isDateRangeObject(value)) return "date";

  // arrays
  if (Array.isArray(value)) {
    if (isStringArray(value)) return "stringArray";
    if (isOptionArray(value)) return "optionArray";
    return "generalArray";
  }

  // unsupported types
  return null;
};

// use this to declare which values should not be added to the final params
const defaultDiscardChecker = (value: unknown) => {
  if (value === null || value === undefined) return true; // remove nulls/undefined
  if (isString(value) && value.length === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true; // remove empty arrays
  if (isDateRangeObject(value) && !value.after && !value.before) return true; // remove empty dates

  return false;
};

// This is the base adapter. It should provide reasonable defaults for the app
// it shouldn't be used directly, but is the template for the factory below
const baseAdapter: QueryAdapter<
  DefaultTransforms,
  FilterSet,
  DjangoQueryParams
> = {
  forceSnakeCase: true,
  paginationAdapter: DjangoPaginationAdapter,

  transforms: defaultTransforms,
  overrides: {},

  getValueType: defaultTypeGetter,
  shouldDiscardValue: defaultDiscardChecker,

  toTableParams({ filters = {}, pagination }) {
    const transformedFilters = Object.entries(filters)
      .filter(([_key, value]) => !this.shouldDiscardValue(value))
      .reduce((acc, [key, value]) => {
        const type = this.getValueType(value);
        if (type === null) return acc; // ignore unknown types

        const snakeKey = this.forceSnakeCase ? toSnakeCase(key) : key;

        const overrideTransform = this.overrides[key];
        const typeTransform = this.transforms[type];
        const transformFn = overrideTransform || typeTransform;

        if (!transformFn) return acc;

        const transformedValue = transformFn(snakeKey, value);

        return { ...acc, ...transformedValue };
      }, {});

    if (pagination && pagination.sort) {
      const field = this.forceSnakeCase
        ? toSnakeCase(pagination.sort.field)
        : pagination.sort.field;
      pagination.sort.field = field;
    }

    const transformedPagination =
      pagination &&
      this.paginationAdapter &&
      this.paginationAdapter.toParams(pagination);

    return { ...transformedFilters, ...transformedPagination };
  },
};

// This is the factory function to create adapters
// Use it to create adapters specific to an api endpoint
// you can pass it overrides in the config object to fine tune its behavior
export const createQueryAdapter = <
  TTransforms extends TransformsMap = DefaultTransforms,
  TFilters extends FilterSet = FilterSet,
  TPaginationFormat = FilterSet,
>(
  config: QueryAdapterConfig<TTransforms, TFilters, TPaginationFormat> = {},
): QueryAdapter<TTransforms, TFilters, TPaginationFormat> => {
  return {
    ...baseAdapter,
    paginationAdapter: config.paginationAdapter,

    transforms: {
      ...baseAdapter.transforms,
      ...config.transforms,
    } as TTransforms,

    overrides: config.overrides ?? ({} as OverridesMap<TFilters>),

    shouldDiscardValue:
      config.valueShouldBeDiscarded ?? baseAdapter.shouldDiscardValue,

    getValueType: (config.getValueType ?? baseAdapter.getValueType) as (
      _value: unknown,
    ) => keyof TTransforms | null,
  };
};
